import { authFetch } from '../../../api/csrf';
import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Phone,
  PhoneCall,
  Clock,
  Search,
  Filter,
  Eye,
  Edit,
  Download,
  Play,
  PhoneOff,
  FileText,
  Settings,
  X,
  Mic,
  Volume2,
  ArrowLeft,
  PhoneIncoming,
  PhoneOutgoing,
  CheckCircle,
  XCircle,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  UserPlus,
  ExternalLink
} from "lucide-react";
import Button from '../../../components/common/Button/Button';
import telephonyService from '../../../api/crm/telephonyService';
import {
  handleTextInputChange,
  handleSearchInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import { formatDate } from '../../../utils/stringUtils';
import ExportModal from '../../../components/common/ExportModal';
import { generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';

import PermissionGate from '../../../components/common/PermissionGate';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { API_ENDPOINTS } from '../../../api/config';


const getFirstDisplayValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');

const parseCallMetadataDate = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const match = value
      .trim()
      .match(/^(\d{2})\/(\d{2})\/(\d{4}),?\s+(\d{2}):(\d{2})(?::(\d{2}))?$/);

    if (match) {
      const [, day, month, year, hour, minute, second = '0'] = match;
      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      );
    }
  }

  return value;
};

/**
 * Correct timestamps for older RP Digital call records that were stored
 * with IST time mis-interpreted as UTC (5h30m ahead of correct value).
 *
 * The backend fix was deployed on 2026-07-20. Records BEFORE that date
 * need a -5h30m correction for display. Records ON OR AFTER that date
 * are already stored correctly and are returned as-is.
 *
 * @param {string|Date} timestamp - The raw performedAt value from the API
 * @returns {Date} Corrected Date object ready for display
 */
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in ms
const RP_DIGITAL_TZ_FIX_DATE = new Date('2026-07-20T00:00:00+05:30');

const correctRPDigitalTimestamp = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return null;
  // Only subtract offset for records stored before the fix was deployed
  if (date < RP_DIGITAL_TZ_FIX_DATE) {
    return new Date(date.getTime() - IST_OFFSET_MS);
  }
  return date;
};

const CallerPopups = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial filter values from URL (for persistence across refresh).
  // Shared across the Missed Calls & Call Logs tabs: search, startDate, endDate.
  // Tab-specific keys are namespaced: reason (missed), status (logs),
  // m* (missed pagination/sort), l* (logs pagination/sort).
  const initialSearch = searchParams.get('search') || '';
  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';
  const initialReason = searchParams.get('reason') || 'all';
  const initialStatus = searchParams.get('status') || 'all';
  const initialMissedPage = Number(searchParams.get('mPage')) || 1;
  const initialMissedLimit = Number(searchParams.get('mLimit')) || 10;
  const initialLogsPage = Number(searchParams.get('lPage')) || 1;
  const initialLogsLimit = Number(searchParams.get('lLimit')) || 10;
  const initialMissedSortBy = searchParams.get('mSortBy') || 'timestamp';
  const initialMissedSortDir = searchParams.get('mSortOrder') || 'desc';
  const initialLogsSortBy = searchParams.get('lSortBy') || 'timestamp';
  const initialLogsSortDir = searchParams.get('lSortOrder') || 'desc';
  const isInitialMissedPageResetMount = useRef(true);
  const isInitialLogsPageResetMount = useRef(true);

  // Persist active tab so it remains the same on refresh (URL takes precedence,
  // then the previously saved localStorage value).
  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = searchParams.get('tab');
    if (fromUrl) return fromUrl;
    const saved = localStorage.getItem('crm_caller_popups_active_tab');
    return saved || "live_console";
  });
  const [incomingCall, setIncomingCall] = useState(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
  const [reasonFilter, setReasonFilter] = useState(initialReason);
  const [statusFilter, setStatusFilter] = useState(initialStatus); // For Call Logs tab
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editCall, setEditCall] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [errors, setErrors] = useState({});
  const searchInputRef = useRef(null);
  const [additionalFilters, setAdditionalFilters] = useState({
    startDate: initialStartDate,
    endDate: initialEndDate,
    priority: "all",
    assignedTo: "all",
  });

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);


  // Helper to map RP Digital call status to a human-readable reason
  const getCallStatusDisplay = (status) => {
    if (!status) return null;
    const statusUpper = status.toUpperCase().trim();
    const statusNormalized = statusUpper.replace(/[_\s-]/g, '-');

    // Check exact matches first
    const statusMap = {
      ANSWER: 'Answered',
      PICKED: 'Answered',
      NOANSWER: 'Missed / No Answer',
      'NO ANSWER': 'Missed / No Answer',
      'NO-ANSWER': 'Missed / No Answer',
      'NOT ANSWERED': 'Missed / No Answer',
      'NOT-ANSWERED': 'Missed / No Answer',
      BUSY: 'Busy',
      'CANCEL-AGENT': 'Cancelled by Agent',
      'CANCEL-CUSTOMER': 'Cancelled by Customer',
      CANCEL: 'Cancelled',
      CANCELLED: 'Cancelled',
      FAILED: 'Failed',
      ABANDONED: 'Abandoned',
      ABANDONEDCALL: 'Abandoned',
      AGENTENGAGED: 'Agent Engaged',
      CONNECTED: 'Answered',
      PENDING: 'Pending'
    };

    // Try exact match first
    if (statusMap[statusUpper]) {
      return statusMap[statusUpper];
    }

    // Try normalized match (handles variations like cancel_customer, cancel customer, etc.)
    if (statusMap[statusNormalized]) {
      return statusMap[statusNormalized];
    }

    // Handle partial matches for cancel variations
    if (statusUpper.includes('CANCEL') || statusUpper.includes('CANCELLED')) {
      if (statusUpper.includes('CUSTOMER') || statusUpper.includes('CLIENT')) {
        return 'Cancelled by Customer';
      }
      if (statusUpper.includes('AGENT') || statusUpper.includes('USER')) {
        return 'Cancelled by Agent';
      }
      return 'Cancelled';
    }

    return status;
  };

  // Autofocus search bar on tab switch
  useEffect(() => {
    if (searchInputRef.current && (activeTab === "missed_calls" || activeTab === "call_logs")) {
      searchInputRef.current.focus();
    }
  }, [activeTab]);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      if (activeTab === 'missed_calls' || activeTab === 'call_logs') {
        setSelectedRows(new Set()); // Reset selection on search
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, activeTab]);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);


  // Check if any filters are active
  const hasActiveFilters =
    searchTerm.trim() !== '' ||
    (activeTab === 'missed_calls' ? reasonFilter !== 'all' : statusFilter !== 'all') ||
    (additionalFilters.startDate || additionalFilters.endDate);

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    if (activeTab === 'missed_calls') {
      setReasonFilter('all');
      setAdditionalFilters({
        startDate: "",
        endDate: "",
        priority: "all",
        assignedTo: "all",
      });
    } else if (activeTab === 'call_logs') {
      setStatusFilter('all');
      setAdditionalFilters({
        startDate: "",
        endDate: "",
        priority: "all",
        assignedTo: "all",
      });
    }
    setIsFilterModalOpen(false);
  };

  const [missedCalls, setMissedCalls] = useState([]);
  const [isMissedCallsLoading, setIsMissedCallsLoading] = useState(false);
  const [missedCallsError, setMissedCallsError] = useState(null);
  const [missedPagination, setMissedPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalActivities: 0,
    limit: 10,
  });
  const [missedCurrentPage, setMissedCurrentPage] = useState(initialMissedPage);
  const [missedItemsPerPage, setMissedItemsPerPage] = useState(initialMissedLimit);

  // Keep Caller Pop-ups & Missed Calls tabs consistent on refresh
  useEffect(() => {
    if (activeTab) {
      localStorage.setItem('crm_caller_popups_active_tab', activeTab);
      setSelectedRows(new Set()); // Reset selection on tab change
    }
  }, [activeTab]);

  const [callLogs, setCallLogs] = useState([]);
  const [isCallLogsLoading, setIsCallLogsLoading] = useState(false);
  const [callLogsError, setCallLogsError] = useState(null);
  const [callLogsPagination, setCallLogsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalActivities: 0,
    limit: 10,
  });
  const [callLogsCurrentPage, setCallLogsCurrentPage] = useState(initialLogsPage);
  const [callLogsItemsPerPage, setCallLogsItemsPerPage] = useState(initialLogsLimit);
  const [isSyncing, setIsSyncing] = useState(false);

  // Trigger manual CDR sync
  const handleSyncCDR = async () => {
    setIsSyncing(true);
    try {
      const result = await telephonyService.syncCDR();
      if (result.success) {
        alert(`Successfully synced ${result.data?.syncedCount || 0} historical calls.`);
        if (activeTab === 'missed_calls') {
          setMissedCurrentPage(1);
        } else {
          setCallLogsCurrentPage(1);
        }
      } else {
        alert('Sync failed: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      alert('An error occurred during sync.');
    } finally {
      setIsSyncing(false);
    }
  };

  // Sorting state for Missed Calls
  const [missedSortColumn, setMissedSortColumn] = useState(initialMissedSortBy);
  const [missedSortDirection, setMissedSortDirection] = useState(initialMissedSortDir);

  // Sorting state for Call Logs
  const [logsSortColumn, setLogsSortColumn] = useState(initialLogsSortBy);
  const [logsSortDirection, setLogsSortDirection] = useState(initialLogsSortDir);

  // Switch tabs and reset every filter/pagination/sort/selection so each tab
  // starts from a clean state (search & date range are shared across tabs).
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);

    // Shared filters
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setAdditionalFilters({
      startDate: "",
      endDate: "",
      priority: "all",
      assignedTo: "all",
    });

    // Missed Calls tab
    setReasonFilter('all');
    setMissedCurrentPage(1);
    setMissedSortColumn('timestamp');
    setMissedSortDirection('desc');

    setStatusFilter('all');
    setCallLogsCurrentPage(1);
    setLogsSortColumn('timestamp');
    setLogsSortDirection('desc');

    setSelectedRows(new Set());
    setIsDetailsModalOpen(false);
    setSelectedCall(null);
    setIsFilterModalOpen(false);
    setIsEditModalOpen(false);
    setEditCall(null);
  };

  // Sync tab + filter state to the URL for persistence across refresh.
  // Filters are kept separate per tab: search/dates are shared between Missed
  // Calls & Call Logs, while reason (missed), status (logs) and each tab's
  // pagination/sort are namespaced so they don't clobber one another.
  // (Declared after all dependent state to avoid a temporal-dead-zone error.)
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = [
        'tab', 'search', 'startDate', 'endDate', 'reason', 'status',
        'mPage', 'mLimit', 'mSortBy', 'mSortOrder',
        'lPage', 'lLimit', 'lSortBy', 'lSortOrder',
      ];
      managedKeys.forEach(key => params.delete(key));

      if (activeTab && activeTab !== 'live_console') params.set('tab', activeTab);
      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (additionalFilters.startDate) params.set('startDate', additionalFilters.startDate);
      if (additionalFilters.endDate) params.set('endDate', additionalFilters.endDate);

      // Missed Calls tab-specific
      if (reasonFilter && reasonFilter !== 'all') params.set('reason', reasonFilter);
      if (missedCurrentPage && missedCurrentPage !== 1) params.set('mPage', String(missedCurrentPage));
      if (missedItemsPerPage && missedItemsPerPage !== 10) params.set('mLimit', String(missedItemsPerPage));
      if (missedSortColumn && missedSortColumn !== 'timestamp') params.set('mSortBy', missedSortColumn);
      if (missedSortDirection && missedSortDirection !== 'desc') params.set('mSortOrder', missedSortDirection);

      // Call Logs tab-specific
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (callLogsCurrentPage && callLogsCurrentPage !== 1) params.set('lPage', String(callLogsCurrentPage));
      if (callLogsItemsPerPage && callLogsItemsPerPage !== 10) params.set('lLimit', String(callLogsItemsPerPage));
      if (logsSortColumn && logsSortColumn !== 'timestamp') params.set('lSortBy', logsSortColumn);
      if (logsSortDirection && logsSortDirection !== 'desc') params.set('lSortOrder', logsSortDirection);

      return params;
    }, { replace: true });
  }, [
    activeTab,
    debouncedSearchTerm,
    additionalFilters.startDate,
    additionalFilters.endDate,
    reasonFilter,
    statusFilter,
    missedCurrentPage,
    missedItemsPerPage,
    missedSortColumn,
    missedSortDirection,
    callLogsCurrentPage,
    callLogsItemsPerPage,
    logsSortColumn,
    logsSortDirection,
    setSearchParams,
  ]);

  const [liveCalls, setLiveCalls] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [liveCallsLastUpdated, setLiveCallsLastUpdated] = useState(null);
  const [liveCallsError, setLiveCallsError] = useState(null);

  // Poll backend for live calls for Live Console
  // Only active when the Live Console tab is selected
  useEffect(() => {
    let isMounted = true;
    let intervalId = null;

    // If Live Console is not active, stop polling and clear live calls
    if (activeTab !== 'live_console') {
      setLiveCalls([]);
      return () => {
        isMounted = false;
        if (intervalId) clearInterval(intervalId);
      };
    }

    const fetchLiveCalls = async () => {
      const result = await telephonyService.getLiveCalls();
      if (!isMounted) return;

      if (result.success) {
        // Ensure liveCalls is an array before mapping
        const liveCallsArray = Array.isArray(result.liveCalls) ? result.liveCalls : [];

        // Backend already returns CRM-mapped structure (caller/agent/activity)
        const mapped = liveCallsArray.map((item, index) => ({
          id: item.id || index,
          customer: item.caller?.name || 'Unknown Caller',
          phone: item.caller?.phone || '',
          agent: item.agent?.name || 'Agent',
          agentPhone: item.agent?.phone || '',
          callStatus: item.callStatus || '',
          status: item.status || 'active',
          direction: item.direction || 'OUT',
          did: item.did || '',
          startTime: item.startedAt || null,
          recordingUrl: item.recordingUrl || '',
          activityId: item.activityId,
          leadId: item.leadId
        }));

        setLiveCalls(mapped);

        // Update recentCalls list (merge, keep last 20 unique by id)
        setRecentCalls((prev) => {
          const existingById = new Map(prev.map(c => [c.id, c]));
          mapped.forEach(call => {
            existingById.set(call.id, { ...existingById.get(call.id), ...call });
          });
          const merged = Array.from(existingById.values());
          // Sort by start time descending
          merged.sort((a, b) => {
            const aTime = a.startTime ? new Date(a.startTime).getTime() : 0;
            const bTime = b.startTime ? new Date(b.startTime).getTime() : 0;
            return bTime - aTime;
          });
          return merged.slice(0, 20);
        });
        setLiveCallsLastUpdated(result.lastUpdated);
        setLiveCallsError(result.lastError || null);
      } else {
        setLiveCalls([]);
        setLiveCallsError(result.error || 'Failed to fetch live calls');
      }
    };

    // Initial fetch and polling every 3 seconds (aligned with backend cache refresh)
    fetchLiveCalls();
    intervalId = setInterval(fetchLiveCalls, 3000);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeTab]);

  // Handle Missed Calls Sorting
  const handleMissedSort = (column) => {
    if (missedSortColumn === column) {
      setMissedSortDirection(missedSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setMissedSortColumn(column);
      setMissedSortDirection('asc');
    }
  };

  // Get sorted Missed Calls
  const getSortedMissedCalls = (data) => {
    if (!missedSortColumn || !data || data.length === 0) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      if (missedSortColumn === 'customer') {
        aValue = (a.customer || '').toLowerCase();
        bValue = (b.customer || '').toLowerCase();
      } else if (missedSortColumn === 'phone') {
        aValue = a.phone || '';
        bValue = b.phone || '';
      } else if (missedSortColumn === 'caller') {
        aValue = (a.caller || '').toLowerCase();
        bValue = (b.caller || '').toLowerCase();
      } else if (missedSortColumn === 'timestamp') {
        aValue = new Date(a.timestamp || 0).getTime();
        bValue = new Date(b.timestamp || 0).getTime();
      } else if (missedSortColumn === 'reason') {
        aValue = (a.reason || '').toLowerCase();
        bValue = (b.reason || '').toLowerCase();
      } else {
        aValue = a[missedSortColumn] || '';
        bValue = b[missedSortColumn] || '';
      }

      const order = missedSortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // Handle Call Logs Sorting
  const handleLogsSort = (column) => {
    if (logsSortColumn === column) {
      setLogsSortDirection(logsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLogsSortColumn(column);
      setLogsSortDirection('asc');
    }
  };

  // Get sorted Call Logs
  const getSortedCallLogs = (data) => {
    if (!logsSortColumn || !data || data.length === 0) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      if (logsSortColumn === 'customer') {
        aValue = (a.customer || '').toLowerCase();
        bValue = (b.customer || '').toLowerCase();
      } else if (logsSortColumn === 'phone') {
        aValue = a.phone || '';
        bValue = b.phone || '';
      } else if (logsSortColumn === 'caller') {
        aValue = (a.performedBy || '').toLowerCase();
        bValue = (b.performedBy || '').toLowerCase();
      } else if (logsSortColumn === 'duration') {
        // Simple string comparison for duration (mm:ss) works for short calls, 
        // but for robustness we could parse it
        aValue = a.duration || '';
        bValue = b.duration || '';
      } else if (logsSortColumn === 'timestamp') {
        aValue = new Date(a.timestamp || 0).getTime();
        bValue = new Date(b.timestamp || 0).getTime();
      } else if (logsSortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else {
        aValue = a[logsSortColumn] || '';
        bValue = b[logsSortColumn] || '';
      }

      const order = logsSortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "missed":
        return "bg-red-100 text-red-800";
      case "answered":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Live stats derived from current live calls
  const liveStats = React.useMemo(() => {
    const incoming = liveCalls.filter(c => c.direction === 'IN').length;
    const outgoing = liveCalls.filter(c => c.direction !== 'IN').length;
    const answered = liveCalls.filter(c => (c.status || '').toLowerCase().includes('answer')).length;
    const noAnswer = liveCalls.filter(c => (c.status || '').toLowerCase().includes('no')).length;
    return { incoming, outgoing, answered, noAnswer };
  }, [liveCalls]);

  const applyFilters = (data) => {
    return data.filter((item) => {
      const matchesSearch =
        debouncedSearchTerm.trim() === '' ||
        item.customer.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        item.business.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (item.caller && item.caller.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        (item.phone && item.phone.includes(debouncedSearchTerm)) ||
        (item.callerPhone && item.callerPhone.includes(debouncedSearchTerm));
      // Reason filtering is now done on the backend
      const matchesReason = activeTab !== "missed_calls";
      const matchesPriority =
        activeTab === "missed_calls" ||
        additionalFilters.priority === "all" ||
        item.priority === additionalFilters.priority;
      const matchesAssignedTo =
        activeTab === "missed_calls" ||
        additionalFilters.assignedTo === "all" ||
        item.assignedTo === additionalFilters.assignedTo;
      const matchesDate =
        (!additionalFilters.startDate ||
          new Date(item.timestamp) >= new Date(additionalFilters.startDate)) &&
        (!additionalFilters.endDate ||
          new Date(item.timestamp) <= new Date(additionalFilters.endDate));
      return (
        matchesSearch &&
        matchesReason &&
        matchesPriority &&
        matchesAssignedTo &&
        matchesDate
      );
    });
  };

  // Backend handles all filtering for missed calls and call logs (search, status, dates)
  const filteredMissedCalls = missedCalls;
  const filteredCallLogs = callLogs; // Backend handles all filtering

  // ** Export Handlers **

  const handleSelectAll = (e) => {
    const data = activeTab === 'missed_calls' ? filteredMissedCalls : filteredCallLogs;
    if (e.target.checked) {
      const allIds = data.map(item => item.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
    newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const requestBackendExportBase = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      filters = {},
      page = 1,
      limit = 10,
      sortBy = 'performedAt',
      sortOrder = 'desc',
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'caller-popups',
            format,
            scope,
            filters,
            page,
            limit,
            sortBy,
            sortOrder,
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `calls-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    []
  );

  const getStatusFilterValue = (status) => {
    if (!status || status === 'all') return undefined;
    const statusMap = {
      'Answered': 'answer,picked,answered',
      'Pending': 'pending',
      'Cancelled by Agent': 'cancel-agent',
      'Cancelled by Customer': 'cancel-customer',
      'Failed': 'failed',
    };
    return statusMap[status] || status;
  };

  const getReasonFilterValue = (reason) => {
    if (!reason || reason === 'all') return 'missed';
    const statusMap = {
      'Cancelled by Agent': 'cancel-agent',
      'Cancelled by Customer': 'cancel-customer',
      'Failed': 'failed',
    };
    return statusMap[reason] || reason;
  };

  const requestBackendExport = useCallback(
    async ({ scope, format, overrideFilters, overridePage, overrideLimit, selectedIds = [] }) => {
      if (exportLoading) return;
      setExportLoading(true);

      try {
        const params = {
          format,
          scope,
          selectedIds,
          filters: overrideFilters ?? {
            search: scope === 'all' ? undefined : (debouncedSearchTerm.trim() || undefined),
            startDate: scope === 'all' ? undefined : (additionalFilters.startDate || undefined),
            endDate: scope === 'all' ? undefined : (additionalFilters.endDate || undefined),
            callStatus: activeTab === 'missed_calls'
              // "missed" is the tab's baseline; on Export All ignore the reason sub-filter
              ? (scope === 'all' ? 'missed' : getReasonFilterValue(reasonFilter))
              : (scope === 'all' ? undefined : getStatusFilterValue(statusFilter)),
            // Tab identity so the backend keeps the Missed-Calls layout/label even when a
            // specific reason sub-filter (e.g. Failed) narrows callStatus away from "missed".
            view: activeTab === 'missed_calls' ? 'missed' : 'logs',
          },
          page: overridePage ?? (activeTab === 'missed_calls' ? missedCurrentPage : callLogsCurrentPage),
          limit: overrideLimit ?? (activeTab === 'missed_calls' ? missedItemsPerPage : callLogsItemsPerPage),
          sortBy: activeTab === 'missed_calls' ? missedSortColumn : logsSortColumn,
          sortOrder: activeTab === 'missed_calls' ? missedSortDirection : logsSortDirection,
        };

        await requestBackendExportBase(params);
        setShowExportModal(false);
      } catch (error) {
        alert(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      exportLoading, 
      activeTab, 
      debouncedSearchTerm, 
      additionalFilters, 
      reasonFilter, 
      statusFilter, 
      missedCurrentPage, 
      callLogsCurrentPage, 
      missedItemsPerPage, 
      callLogsItemsPerPage,
      missedSortColumn,
      logsSortColumn,
      missedSortDirection,
      logsSortDirection,
      requestBackendExportBase
    ]
  );


  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleCallerPopupsSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      if (!selectedRows.size) {
        alert('No selected items found to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: Array.from(selectedRows),
        });
        setSelectedRows(new Set());
        setShowInlineSelectedFormats(false);
      } catch (error) {
        alert(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleCallerPopupsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        const params = { scope, format };

        if (scope === 'dateRange' && meta.startDate && meta.endDate) {
          params.overrideFilters = {
            startDate: meta.startDate,
            endDate: meta.endDate,
            callStatus: activeTab === 'missed_calls' ? 'missed' : (statusFilter !== 'all' ? statusFilter : undefined),
            view: activeTab === 'missed_calls' ? 'missed' : 'logs',
          };
        }

        await requestBackendExport(params);
        setShowExportModal(false);
      } catch (error) {
        alert(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [requestBackendExport, activeTab, statusFilter]
  );



  // Clear selection on page change or filter change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [missedCurrentPage, callLogsCurrentPage, debouncedSearchTerm, reasonFilter, statusFilter, additionalFilters]);


  const handleIncomingCall = (call) => {
    setIncomingCall(call);
    setTimeout(() => {
      setIncomingCall(null);
    }, 10000);
  };

  const handleAnswerCall = () => {
    setIsCallActive(true);
    setIncomingCall(null);
  };

  const handleEndCall = () => {
    setIsCallActive(false);
  };

  // Reset missed calls page when filters change (skip on initial mount so the
  // URL-restored page is preserved on refresh)
  useEffect(() => {
    if (isInitialMissedPageResetMount.current) {
      isInitialMissedPageResetMount.current = false;
      return;
    }
    if (activeTab === 'missed_calls') {
      setMissedCurrentPage(1);
    }
  }, [activeTab, debouncedSearchTerm, additionalFilters.startDate, additionalFilters.endDate, reasonFilter]);

  // Helper to map frontend reason filter to backend callStatus
  const mapReasonFilterToCallStatus = (reasonFilter) => {
    if (!reasonFilter || reasonFilter === 'all') {
      return 'missed'; // Default: get all missed calls
    }

    const reasonMap = {
      'Cancelled by Agent': 'cancel-agent',
      'Cancelled by Customer': 'cancel-customer',
      'Failed': 'failed',
    };

    return reasonMap[reasonFilter] || 'missed';
  };

  // Fetch missed calls from CRM with server-side pagination, search, and filters
  useEffect(() => {
    let isMounted = true;

    const fetchMissedCalls = async () => {
      setIsMissedCallsLoading(true);
      setMissedCallsError(null);

      const result = await telephonyService.getMissedCalls({
        search: debouncedSearchTerm || undefined,
        startDate: additionalFilters.startDate || undefined,
        endDate: additionalFilters.endDate || undefined,
        callStatus: mapReasonFilterToCallStatus(reasonFilter),
        page: missedCurrentPage,
        limit: missedItemsPerPage
      });

      if (!isMounted) return;

      if (result.success) {
        const activities = Array.isArray(result.activities) ? result.activities : [];

        // Helper to normalize phone numbers to last 10 digits (remove country code)
        const normalizePhone = (value) => {
          if (!value) return '';
          const digits = value.toString().replace(/\D/g, '');
          if (!digits) return '';
          return digits.slice(-10);
        };

        const mapped = activities.map((activity) => {
          const lead = activity.lead || {};
          const performer = activity.performedBy || {};
          const statusRaw = activity.callMetadata?.status || '';
          const statusLower = statusRaw.toLowerCase();

          // Map low-level call status to UI status
          let uiStatus = 'completed';
          if (statusLower.includes('answer') || statusLower === 'answer' || statusLower === 'answered' || statusLower === 'picked') {
            uiStatus = 'answered';
          } else if (
            statusLower === 'noanswer' ||
            statusLower === 'no answer' ||
            statusLower === 'no-answer' ||
            statusLower === 'not answered' ||
            statusLower === 'not-answered' ||
            statusLower === 'missed' ||
            statusLower === 'busy' ||
            statusLower === 'cancel' ||
            statusLower === 'cancelled' ||
            statusLower === 'cancel-agent' ||
            statusLower === 'cancel-customer' ||
            statusLower === 'failed' ||
            statusLower === 'abandoned'
          ) {
            uiStatus = 'missed';
          }

          const callMetadata = activity.callMetadata || {};
          const isIncoming = (callMetadata.direction || '').toUpperCase().includes('IN');

          // Identify customer phone from metadata if no lead/customer
          const customerPhoneFromMetadata = isIncoming
            ? (callMetadata.from || callMetadata.sourceNumber || callMetadata.SourceNumber || callMetadata.callerNumber)
            : (callMetadata.to || callMetadata.destinationNumber || callMetadata.DestinationNumber || callMetadata.calleeNumber);

          // Handle both lead and customer data, and unmapped calls
          const customer = activity.customer || {};
          let customerName, businessName, entityPhone, entityId, entityType;
          
          if (lead._id || lead.leadId) {
            // Lead activity
            customerName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() ||
              lead.company ||
              lead.email ||
              callMetadata.receiver_name ||
              (customerPhoneFromMetadata ? normalizePhone(customerPhoneFromMetadata) : 'Unknown Caller');
            businessName = lead.company || '';
            entityPhone = lead.phone;
            entityId = lead._id || lead.leadId;
            entityType = 'lead';
          } else if (customer._id || customer.customerId) {
            // Customer activity
            const contactPerson = customer.contactPerson?.trim() || '';
            const companyName = customer.companyName?.trim() || '';
            
            // Use contact person as primary name, fallback to company
            customerName = contactPerson || companyName || customer.customerId || 'Unknown';
            
            // Only show company name in business line if it's different from contact person
            businessName = (companyName && companyName !== contactPerson) ? companyName : '';
            
            entityPhone = customer.phone;
            entityId = customer._id || customer.customerId;
            entityType = 'customer';
          } else {
            // Unmapped call - show N/A
            customerName = 'N/A';
            businessName = '';
            entityPhone = null;
            entityId = null;
            entityType = 'unmapped';
          }

          const assignedTo =
            `${performer.firstName || ''} ${performer.lastName || ''}`.trim() ||
            performer.email ||
            'System';

          const callerPhoneRaw =
            performer.phone ||
            (isIncoming ? callMetadata.calleeNumber : callMetadata.callerNumber) ||
            '';

          // Derive ringing time in seconds from call metadata
          const talkDurationSeconds = typeof callMetadata.talkDuration === 'number' ? callMetadata.talkDuration : (typeof callMetadata.duration === 'number' ? callMetadata.duration : (typeof callMetadata.duration === 'string' ? parseInt(callMetadata.duration) : null));
          const callDurationSeconds = typeof callMetadata.callDuration === 'number' ? callMetadata.callDuration : talkDurationSeconds;
          let ringingSeconds = null;

          if (callDurationSeconds !== null) {
            if (talkDurationSeconds !== null && talkDurationSeconds >= 0) {
              ringingSeconds = Math.max(callDurationSeconds - talkDurationSeconds, 0);
            } else {
              ringingSeconds = callDurationSeconds;
            }
          }


          return {
            id: activity._id,
            customer: customerName,
            business: businessName,
            // Display phone numbers without country code (last 10 digits)
            phone: normalizePhone(entityPhone || customerPhoneFromMetadata || ''),
            timestamp: correctRPDigitalTimestamp(activity.performedAt),
            // Format duration using same logic as Lead 360 activities timeline:
            // ONLY use actual talk time from RP Digital (talkDuration in seconds)
            duration: (() => {
              const talkDurationRaw = callMetadata.talkDuration || callMetadata.duration || 0;
              const talkDuration = typeof talkDurationRaw === 'number' && talkDurationRaw > 0
                ? Math.floor(talkDurationRaw)
                : (typeof talkDurationRaw === 'string' ? parseInt(talkDurationRaw) : 0);

              if (!talkDuration || talkDuration <= 0) return '00:00';

              const hours = Math.floor(talkDuration / 3600);
              const mins = Math.floor((talkDuration % 3600) / 60);
              const secs = talkDuration % 60;

              if (hours > 0) {
                return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
              }
              return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            })(),
            status: uiStatus,
            reason: getCallStatusDisplay(statusRaw) || uiStatus,
            // Ringing time derived from callDuration - talkDuration (in seconds)
            ringingTimeSeconds: ringingSeconds,
            notes: activity.notes || activity.description || '',
            caller: assignedTo, // The person who called (agent/user)
            callerPhone: normalizePhone(callerPhoneRaw),        // The caller's phone number (normalized)
            // For now, treat all missed calls as needing callback
            callbackRequired: uiStatus === 'missed',
            // Recording URL (new API uses 'file', webhooks use 'callRecordingUrl')
            recording: callMetadata.file || callMetadata.callRecordingUrl || callMetadata.recordingUrl || callMetadata.recording || null,
            // Recording duration (talkDuration - actual recorded talk time in seconds)
            recordingDuration: (() => {
              const totalSeconds = Math.floor(callMetadata.talkDuration || callMetadata.duration || 0);
              if (totalSeconds <= 0) return null;
              const hours = Math.floor(totalSeconds / 3600);
              const mins = Math.floor((totalSeconds % 3600) / 60);
              const secs = totalSeconds % 60;
              if (hours > 0) {
                return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
              }
              return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            })(),
            activityId: activity._id,
            leadId: (lead._id || lead.leadId) ? (lead._id || lead.leadId) : null,
            customerId: (customer._id || customer.customerId) ? (customer._id || customer.customerId) : null,
            entityType,
            // Store raw metadata for detailed view
            callMetadata: callMetadata,
            title: activity.title || 'Call Activity',
            description: activity.description || activity.notes || '',
          };
        });

        setMissedCalls(mapped);
        setMissedPagination(result.pagination || {});
      } else {
        setMissedCalls([]);
        setMissedCallsError(result.error || 'Failed to load missed calls');
        setMissedPagination({
          currentPage: 1,
          totalPages: 1,
          totalActivities: 0,
          limit: missedItemsPerPage,
        });
      }

      setIsMissedCallsLoading(false);
    };

    if (activeTab === 'missed_calls') {
      fetchMissedCalls();
    }

    return () => {
      isMounted = false;
    };
  }, [activeTab, debouncedSearchTerm, additionalFilters.startDate, additionalFilters.endDate, missedCurrentPage, missedItemsPerPage, reasonFilter]);

  // Reset call logs page when filters change (skip on initial mount so the
  // URL-restored page is preserved on refresh)
  useEffect(() => {
    if (isInitialLogsPageResetMount.current) {
      isInitialLogsPageResetMount.current = false;
      return;
    }
    if (activeTab === 'call_logs') {
      setCallLogsCurrentPage(1);
    }
  }, [activeTab, debouncedSearchTerm, statusFilter, additionalFilters.startDate, additionalFilters.endDate]);

  // Fetch call logs from CRM with server-side pagination, search, and filters
  useEffect(() => {
    let isMounted = true;

    const fetchCallLogs = async () => {
      setIsCallLogsLoading(true);
      setCallLogsError(null);

      // Map status filter to callStatus for backend (RP Digital status values)
      const mapStatusFilterToCallStatus = (statusFilter) => {
        if (!statusFilter || statusFilter === 'all') {
          return undefined; // Get all statuses
        }
        // Map frontend filter values to RP Digital status values
        // Backend supports comma-separated values for multiple matches
        const statusMap = {
          'Answered': 'answer,picked', // RP Digital uses both 'answer' and 'picked' for answered calls
          'Pending': 'pending',
          'Cancelled by Agent': 'cancel-agent',
          'Cancelled by Customer': 'cancel-customer',
          'Failed': 'failed',
        };
        return statusMap[statusFilter] || statusFilter;
      };

      const result = await telephonyService.getCallLogs({
        search: debouncedSearchTerm || undefined,
        startDate: additionalFilters.startDate || undefined,
        endDate: additionalFilters.endDate || undefined,
        callStatus: mapStatusFilterToCallStatus(statusFilter), // Use callStatus for RP Digital status filtering
        page: callLogsCurrentPage,
        limit: callLogsItemsPerPage
      });

      if (!isMounted) return;

      if (result.success) {
        const activities = Array.isArray(result.activities) ? result.activities : [];

        // Helper to format duration to standard format (HH:MM:SS or MM:SS)
        // Use ONLY talkDuration (actual talk time in seconds) so it matches Lead 360 activities timeline
        const formatDuration = (activity) => {
          const callMetadata = activity.callMetadata || {};
          const talkDurationRaw = callMetadata.talkDuration || callMetadata.duration || 0;
          const talkDuration = typeof talkDurationRaw === 'number' && talkDurationRaw > 0
            ? Math.floor(talkDurationRaw)
            : (typeof talkDurationRaw === 'string' ? parseInt(talkDurationRaw) : 0);

          if (!talkDuration || talkDuration <= 0) return '00:00';

          const hours = Math.floor(talkDuration / 3600);
          const mins = Math.floor((talkDuration % 3600) / 60);
          const secs = talkDuration % 60;

          if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
          }
          return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        };

        // Helper to determine call type (inbound/outbound) from callMetadata
        const getCallType = (callMetadata) => {
          if (!callMetadata) return 'outbound';
          const direction = callMetadata.direction || '';
          const directionLower = direction.toLowerCase();
          if (directionLower.includes('in') || directionLower === 'inbound' || directionLower === 'in') {
            return 'inbound';
          }
          return 'outbound';
        };

        // Helper to get call status
        const getCallStatus = (activity) => {
          const statusRaw = activity.callMetadata?.status || '';
          const statusLower = statusRaw.toLowerCase();
          const outcome = activity.outcome || '';

          if (outcome === 'completed' || statusLower.includes('answer') || statusLower === 'answered' || statusLower === 'picked') {
            return 'completed';
          } else if (
            statusLower === 'noanswer' ||
            statusLower === 'no answer' ||
            statusLower === 'missed' ||
            statusLower === 'busy' ||
            statusLower === 'cancel' ||
            statusLower === 'cancelled' ||
            statusLower === 'failed'
          ) {
            return 'missed';
          } else if (statusLower === 'answered' || statusLower === 'picked') {
            return 'answered';
          }
          return 'completed'; // Default
        };

        // Helper to normalize phone numbers to last 10 digits (remove country code)
        const normalizePhone = (value) => {
          if (!value) return '';
          const digits = value.toString().replace(/\D/g, '');
          if (!digits) return '';
          return digits.slice(-10);
        };

        const mapped = activities.map((activity) => {
          const lead = activity.lead || {};
          const customer = activity.customer || {};
          const performer = activity.performedBy || {};
          const callMetadata = activity.callMetadata || {};

          const isIncoming = getCallType(callMetadata) === 'inbound';

          // Identify customer phone from metadata if no lead/customer
          const customerPhoneFromMetadata = isIncoming
            ? (callMetadata.from || callMetadata.sourceNumber || callMetadata.SourceNumber || callMetadata.callerNumber)
            : (callMetadata.to || callMetadata.destinationNumber || callMetadata.DestinationNumber || callMetadata.calleeNumber);

          // Handle both lead and customer data, and unmapped calls
          let customerName, businessName, entityPhone, entityId, entityType;
          
          if (lead._id || lead.leadId) {
            // Lead activity
            customerName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() ||
              lead.company ||
              lead.email ||
              callMetadata.receiver_name ||
              (customerPhoneFromMetadata ? normalizePhone(customerPhoneFromMetadata) : 'Unknown');
            businessName = lead.company || '';
            entityPhone = lead.phone;
            entityId = lead._id || lead.leadId;
            entityType = 'lead';
          } else if (customer._id || customer.customerId) {
            // Customer activity
            const contactPerson = customer.contactPerson?.trim() || '';
            const companyName = customer.companyName?.trim() || '';
            
            // Use contact person as primary name, fallback to company
            customerName = contactPerson || companyName || customer.customerId || 'Unknown';
            
            // Only show company name in business line if it's different from contact person
            businessName = (companyName && companyName !== contactPerson) ? companyName : '';
            
            entityPhone = customer.phone;
            entityId = customer._id || customer.customerId;
            entityType = 'customer';
          } else {
            // Unmapped call - show N/A
            customerName = 'N/A';
            businessName = '';
            entityPhone = null;
            entityId = null;
            entityType = 'unmapped';
          }

          // Get RP Digital status from callMetadata
          const rpDigitalStatus = callMetadata.status || '';

          return {
            id: activity._id || activity.activityId,
            customer: customerName,
            business: businessName,
            phone: normalizePhone(entityPhone || customerPhoneFromMetadata || ''),
            type: getCallType(callMetadata),
            timestamp: correctRPDigitalTimestamp(activity.performedAt || activity.createdAt),
            duration: formatDuration(activity), // Pass full activity object for better duration calculation
            status: getCallStatus(activity),
            rpDigitalStatus: rpDigitalStatus, // Store raw RP Digital status
            disposition: activity.notes || activity.description || 'No notes',
            notes: activity.notes || activity.description || '',
            recording: callMetadata.file || callMetadata.callRecordingUrl || callMetadata.recordingUrl || callMetadata.recording || null,
            // Recording duration
            recordingDuration: (() => {
              const totalSeconds = Math.floor(callMetadata.talkDuration || callMetadata.duration || 0);
              if (totalSeconds <= 0) return null;
              const hours = Math.floor(totalSeconds / 3600);
              const mins = Math.floor((totalSeconds % 3600) / 60);
              const secs = totalSeconds % 60;
              if (hours > 0) {
                return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
              }
              return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            })(),
            activityId: activity._id || activity.activityId,
            leadId: lead._id || lead.leadId,
            customerId: customer._id || customer.customerId,
            entityType: entityType,
            performedBy: performer ? `${performer.firstName || ''} ${performer.lastName || ''}`.trim() || performer.email : 'Unknown',
            // Store raw metadata for detailed view
            callMetadata: callMetadata,
            title: activity.title || 'Call Activity',
            description: activity.description || activity.notes || '',
          };
        });

        setCallLogs(mapped);
        setCallLogsPagination(result.pagination || {});
      } else {
        setCallLogs([]);
        setCallLogsError(result.error || 'Failed to load call logs');
        setCallLogsPagination({
          currentPage: 1,
          totalPages: 1,
          totalActivities: 0,
          limit: callLogsItemsPerPage,
        });
      }

      setIsCallLogsLoading(false);
    };

    if (activeTab === 'call_logs') {
      fetchCallLogs();
    }

    return () => {
      isMounted = false;
    };
  }, [activeTab, debouncedSearchTerm, callLogsCurrentPage, callLogsItemsPerPage, statusFilter, additionalFilters.startDate, additionalFilters.endDate]);

  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = () => {
    setIsFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    setAdditionalFilters({
      startDate: "",
      endDate: "",
      priority: "all",
      assignedTo: "all",
    });
    setIsFilterModalOpen(false);
  };

  const handleExport = () => {
    const data =
      activeTab === "missed_calls" ? filteredMissedCalls : filteredCallLogs;
    const headers =
      activeTab === "missed_calls"
        ? [
          "Customer",
          "Business",
          "Phone",
          "Caller",
          "Time",
          "Reason",
          "Ring Duration (sec)",
          "Notes",
        ]
        : [
          "Customer",
          "Business",
          "Phone",
          "Type",
          "Time",
          "Duration",
          "Status",
          "Disposition",
          "Notes",
        ];
    const csvRows = [
      headers.join(","),
      ...data.map((item) =>
        [
          `"${item.customer}"`,
          `"${item.business}"`,
          item.phone,
          activeTab === "missed_calls"
            ? `"${item.caller || ""}"`
            : `"${item.type || ""}"`,
          `"${formatDate(item.timestamp, { includeTime: true })}"`,
          activeTab === "missed_calls"
            ? `"${item.reason || getCallStatusDisplay(item.status) || ""}"`
            : "",
          activeTab === "missed_calls"
            ? (typeof item.ringingTimeSeconds === 'number' ? Math.round(item.ringingTimeSeconds) : "")
            : item.status,
          `"${item.notes || ""}"`,
          activeTab === "missed_calls"
            ? ""
            : `"${item.disposition || ""}"`,
        ]
          .filter((field, index) => !(activeTab === "missed_calls" && index === 5 && field === ""))
          .join(",")
      ),
    ];
    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${activeTab}_export_${new Date().toISOString()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSettings = () => {
    setActiveTab("settings");
  };

  // Action Button Handlers
  const handleCallback = useCallback((call) => {
    alert(`Initiating callback to ${call.customer} (${call.phone})`);
  }, []);

  const handleViewDetails = useCallback((item) => {
    setSelectedCall({ ...item, location: "Unknown Location" });
    setIsDetailsModalOpen(true);
  }, []);

  const formatCallMetadataDate = useCallback((...values) => {
    const value = getFirstDisplayValue(...values);
    if (!value) return 'N/A';

    return formatDate(parseCallMetadataDate(value), { includeTime: true }) || 'N/A';
  }, []);

  const handleCloseDetailsModal = useCallback(() => {
    setIsDetailsModalOpen(false);
    setSelectedCall(null);
  }, []);

  const handleEditCall = useCallback((call) => {
    setEditCall({ ...call });
    setIsEditModalOpen(true);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editCall) return;

    // Validate required fields
    const requiredFields = ['notes'];
    const validation = validateRequiredFields(editCall, requiredFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setErrors({});
    setMissedCalls((prev) =>
      prev.map((call) =>
        call.id === editCall.id ? { ...call, ...editCall } : call
      )
    );
    setIsEditModalOpen(false);
    setEditCall(null);
  }, [editCall]);

  const handlePlayRecording = useCallback((log) => {
    if (log.recording) {
      // If recording is a URL, open it in a new tab
      if (typeof log.recording === 'string' && (log.recording.startsWith('http://') || log.recording.startsWith('https://'))) {
        window.open(log.recording, '_blank');
      } else {
        // Otherwise, show alert with recording info
        alert(`Playing recording: ${log.recording}`);
      }
    }
  }, []);

  const handleDownloadRecording = useCallback((log) => {
    alert(`Downloading recording: ${log.recording}`);
  }, []);

  const tabs = [
    { id: "live_console", label: "Live Console", icon: Phone },
    { id: "missed_calls", label: "Missed Call Queue", icon: Clock },
    { id: "call_logs", label: "Call Logs", icon: FileText },
  ];

  return (
    <div className="p-6 mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Caller Pop-ups & Missed Calls
              </h1>
              <p className="text-gray-600 mt-2">
                Manage missed call queue, and call logs
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === tab.id
                    ? "border-agrosaf-green text-agrosaf-green"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Live Console Tab */}
      {activeTab === "live_console" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Live Console
            </h3>
            {liveCallsError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
                {liveCallsError}
              </div>
            )}
            {liveCalls.length === 0 && !liveCallsError ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Phone className="h-6 w-6 text-blue-600" />
                  <div>
                    <h4 className="text-lg font-medium text-blue-900">
                      No live calls right now
                    </h4>
                    <p className="text-blue-700 text-sm">
                      When calls are in progress on CRM, they will appear here automatically.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {liveCalls.map((call) => (
                  <div
                    key={call.id}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  >
                    {/* Header row: status + high-level summary */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                        <span className="text-xs font-semibold uppercase tracking-wide text-green-700">
                          Active Call
                        </span>
                        {call.callStatus && (
                          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                            {call.callStatus}
                          </span>
                        )}
                      </div>
                      {call.startTime && (
                        <p className="text-xs text-gray-500">
                          Started:&nbsp;
                          {new Date(call.startTime).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </p>
                      )}
                    </div>

                    {/* Body: two-column layout for Caller vs Agent */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Caller / Lead details */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">
                          Caller (Lead / Customer)
                        </p>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {call.customer}
                        </p>
                        <p className="text-xs text-gray-600">
                          {call.phone || '-'}
                        </p>
                        {call.did && (
                          <p className="mt-1 text-xs text-gray-400">
                            DID: {call.did}
                          </p>
                        )}
                      </div>

                      {/* Agent details + actions */}
                      <div className="md:text-right">
                        <p className="text-xs font-semibold text-gray-500 mb-1">
                          Agent (CRM User)
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {call.agent}
                        </p>
                        {call.agentPhone && (
                          <p className="text-xs text-gray-600">
                            {call.agentPhone}
                          </p>
                        )}
                        {call.recordingUrl && (
                          <div className="mt-2 flex md:justify-end">
                            <button
                              onClick={() => window.open(call.recordingUrl, '_blank')}
                              className="inline-flex items-center px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-100 transition-colors"
                              title="Open recording in new tab"
                            >
                              <Play className="h-3 w-3 mr-1" />
                              Recording
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Missed Call Queue Tab */}
      {activeTab === "missed_calls" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
              <div className="flex flex-1 gap-3 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    ref={activeTab === "missed_calls" ? searchInputRef : null}
                    type="text"
                    placeholder="Search missed calls..."
                    value={searchTerm}
                    onChange={(e) => handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search)}
                    maxLength={FIELD_LIMITS.search}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
                    autoComplete="off"
                    autoFocus={activeTab === "missed_calls"}
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                  <select
                    value={reasonFilter}
                    onChange={(e) => setReasonFilter(e.target.value)}
                    className="w-full max-w-sm min-w-[180px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Reasons</option>
                    <option value="Cancelled by Agent">Cancelled by Agent</option>
                    <option value="Cancelled by Customer">Cancelled by Customer</option>
                    <option value="Failed">Failed</option>
                  </select>
                  <DateRangeFilter
                    startDate={additionalFilters.startDate}
                    endDate={additionalFilters.endDate}
                    onChange={(newRange) => {
                      setAdditionalFilters((prev) => ({ ...prev, ...newRange }));
                      setMissedCurrentPage(1);
                    }}
                    allowFutureDates={false}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <PermissionGate permission="telephony_logs" action="write">
                  <button
                    onClick={handleSyncCDR}
                    disabled={isSyncing}
                    className="flex items-center px-4 py-2 text-white bg-agrosaf-green rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                    {isSyncing ? 'Syncing...' : 'Sync All'}
                  </button>
                </PermissionGate>

                <PermissionGate permission="telephony_logs" action="read">
                  <div className="relative" ref={selectedExportWrapRef}>
                    <button
                      onClick={handleExportClick}
                      className={`flex items-center px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                        selectedRows.size > 0
                          ? 'bg-blue-50 border-2 border-blue-200 text-blue-600'
                          : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                      }`}
                      disabled={exportLoading || (missedCalls.length === 0 && selectedRows.size === 0)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
                    </button>
                    <SelectedExportFormatBar
                      isOpen={showInlineSelectedFormats}
                      panelEntered={inlineFormatPanelEntered}
                      onExport={handleCallerPopupsSelectedFormatExport}
                      loading={exportLoading}
                      formats={['csv', 'xlsx', 'pdf']}
                    />
                  </div>
                </PermissionGate>

              </div>
            </div>
          </div>

          {/* Missed Calls List */}
          <div className="bg-white rounded-lg shadow-sm border">
            {isMissedCallsLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="flex items-center space-x-2">
                  <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
                  <span className="text-gray-600">
                    Loading missed calls...
                  </span>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={filteredMissedCalls.length > 0 && filteredMissedCalls.every(item => selectedRows.has(item.id))}
                          onChange={handleSelectAll}
                          disabled={filteredMissedCalls.length === 0}
                        />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleMissedSort('customer')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Customer</span>
                          {missedSortColumn === 'customer' ? (
                            missedSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleMissedSort('phone')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Phone</span>
                          {missedSortColumn === 'phone' ? (
                            missedSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleMissedSort('timestamp')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Time</span>
                          {missedSortColumn === 'timestamp' ? (
                            missedSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleMissedSort('reason')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Reason</span>
                          {missedSortColumn === 'reason' ? (
                            missedSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredMissedCalls.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-6 py-6 text-center text-sm text-gray-500"
                        >
                          No missed calls found for the current filters.
                        </td>
                      </tr>
                    ) : (
                      getSortedMissedCalls(filteredMissedCalls).map((call) => (
                        <tr key={call.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              checked={selectedRows.has(call.id)}
                              onChange={() => handleSelectRow(call.id)}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="min-w-0 max-w-xs transition-all">
                              {call.leadId ? (
                                <button
                                  onClick={() => navigate(`/lead_detail_360/${call.leadId}`)}
                                  className="text-sm font-semibold text-agrosaf-green hover:underline capitalize truncate flex items-center gap-1 group"
                                >
                                  {call.customer}
                                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                              ) : call.customerId ? (
                                <button
                                  onClick={() => navigate(`/customer_360/${call.customerId}`)}
                                  className="text-sm font-semibold text-agrosaf-green hover:underline capitalize truncate flex items-center gap-1 group"
                                >
                                  {call.customer}
                                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                              ) : (
                                <div className="text-sm font-semibold text-gray-900 capitalize truncate">
                                  {call.customer}
                                </div>
                              )}
                              <div className="text-sm text-gray-500 capitalize truncate">
                                {call.business}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {call.phone || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(call.timestamp, { includeTime: true })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${call.status === 'missed' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                              {call.reason || getCallStatusDisplay(call.status) || '-'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              {call.leadId ? (
                                <button
                                  onClick={() => navigate(`/lead_detail_360/${call.leadId}`)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-agrosaf-green text-xs font-medium rounded text-agrosaf-green bg-white hover:bg-green-50 transition-colors"
                                  title="View Lead"
                                >
                                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                  Lead
                                </button>
                              ) : call.customerId ? (
                                <button
                                  onClick={() => navigate(`/customer_360/${call.customerId}`)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-agrosaf-green text-xs font-medium rounded text-agrosaf-green bg-white hover:bg-green-50 transition-colors"
                                  title="View Customer"
                                >
                                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                  Customer
                                </button>
                              ) : (
                                <button
                                  onClick={() => navigate(`/manual_lead_entry?phone=${call.phone}`)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-blue-600 text-xs font-medium rounded text-blue-600 bg-white hover:bg-blue-50 transition-colors"
                                  title="Add as Lead"
                                >
                                  <UserPlus className="h-3.5 w-3.5 mr-1" />
                                  Add Lead
                                </button>
                              )}
                              {call.recording ? (
                                <button
                                  onClick={() => handlePlayRecording(call)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-green-600 text-xs font-medium rounded text-green-600 bg-white hover:bg-green-50 transition-colors"
                                  title="Play Recording"
                                >
                                  <Play className="h-3.5 w-3.5" />
                                </button>
                              ) : (
                                <span className="text-gray-300 -mr-1">
                                  <PhoneOff className="h-4 w-4" />
                                </span>
                              )}
                              <button
                                onClick={() => handleViewDetails(call)}
                                className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                title="View Call Details"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Missed Calls Pagination */}
          {(() => {
            const totalItems = Number(missedPagination?.totalActivities || missedCalls.length || 0);
            const totalPages = Number(missedPagination?.totalPages || 1);
            const effectiveLimit = missedPagination?.limit || missedItemsPerPage || 10;
            const shouldShowPagination = !isMissedCallsLoading && totalPages > 1;

            const handleMissedPageChange = (page) => {
              setMissedCurrentPage(page);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            };

            return shouldShowPagination ? (
              <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                  {/* Results Count and Limit Selector */}
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-700">
                      Showing {((missedPagination.currentPage || 1) - 1) * effectiveLimit + 1} to {Math.min((missedPagination.currentPage || 1) * effectiveLimit, totalItems)} of {totalItems} results
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700">Items per page:</label>
                      <select
                        value={missedItemsPerPage}
                        onChange={(e) => {
                          setMissedItemsPerPage(Number(e.target.value));
                          setMissedCurrentPage(1);
                        }}
                        className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleMissedPageChange((missedPagination.currentPage || 1) - 1)}
                      disabled={missedPagination.hasPrevPage === false || (missedPagination.currentPage || 1) <= 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {(() => {
                        const totalPagesInner = missedPagination.totalPages || 1;
                        const currentPageNum = missedPagination.currentPage || 1;
                        const pages = [];

                        if (totalPagesInner <= 10) {
                          for (let i = 1; i <= totalPagesInner; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push(1);

                          if (currentPageNum <= 4) {
                            for (let i = 2; i <= 5; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPagesInner);
                          } else if (currentPageNum >= totalPagesInner - 3) {
                            pages.push('ellipsis');
                            for (let i = totalPagesInner - 4; i <= totalPagesInner; i++) {
                              pages.push(i);
                            }
                          } else {
                            pages.push('ellipsis');
                            for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPagesInner);
                          }
                        }

                        return pages.map((page, index) => {
                          if (page === 'ellipsis') {
                            return (
                              <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                ...
                              </span>
                            );
                          }

                          return (
                            <button
                              key={page}
                              onClick={() => handleMissedPageChange(page)}
                              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                ? 'bg-agrosaf-green text-white'
                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                              {page}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      onClick={() => handleMissedPageChange((missedPagination.currentPage || 1) + 1)}
                      disabled={missedPagination.hasNextPage === false || (missedPagination.currentPage || 1) >= (missedPagination.totalPages || 1)}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            ) : null;
          })()}
        </div>
      )}

      {/* Call Logs Tab */}
      {activeTab === "call_logs" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={activeTab === "call_logs" ? searchInputRef : null}
                  type="text"
                  placeholder="Search call logs..."
                  value={searchTerm}
                  onChange={(e) => handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search)}
                  maxLength={FIELD_LIMITS.search}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
                  autoComplete="off"
                  autoFocus={activeTab === "call_logs"}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full max-w-sm min-w-[180px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="Answered">Answered</option>
                  <option value="Pending">Pending</option>
                  <option value="Cancelled by Agent">Cancelled by Agent</option>
                  <option value="Cancelled by Customer">Cancelled by Customer</option>
                  <option value="Failed">Failed</option>
                </select>
                <DateRangeFilter
                  startDate={additionalFilters.startDate}
                  endDate={additionalFilters.endDate}
                  onChange={(newRange) => {
                    setAdditionalFilters((prev) => ({ ...prev, ...newRange }));
                    setCallLogsCurrentPage(1);
                  }}
                  allowFutureDates={false}
                />
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    icon={<X className="h-4 w-4" />}
                    onClick={handleClearFilters}
                    className="flex-shrink-0 whitespace-nowrap"
                  >
                    Clear
                  </Button>
                )}
              </div>

              <PermissionGate permission="telephony_logs" action="read">
                <div className="relative" ref={selectedExportWrapRef}>
                  <button
                    onClick={handleExportClick}
                    className={`flex items-center px-4 py-2 rounded-lg transition-colors whitespace-nowrap ml-3 ${
                      selectedRows.size > 0
                        ? 'bg-blue-50 border-2 border-blue-200 text-blue-600'
                        : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                    }`}
                    disabled={exportLoading || (callLogs.length === 0 && selectedRows.size === 0)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
                  </button>
                  <SelectedExportFormatBar
                    isOpen={showInlineSelectedFormats}
                    panelEntered={inlineFormatPanelEntered}
                    onExport={handleCallerPopupsSelectedFormatExport}
                    loading={exportLoading}
                    formats={['csv', 'xlsx', 'pdf']}
                  />
                </div>
              </PermissionGate>

            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border">
            {isCallLogsLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="flex items-center space-x-2">
                  <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
                  <span className="text-gray-600">
                    Loading call logs...
                  </span>
                </div>
              </div>
            ) : callLogsError ? (
              <div className="text-center py-10">
                <p className="text-red-600">{callLogsError}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={filteredCallLogs.length > 0 && filteredCallLogs.every(item => selectedRows.has(item.id))}
                          onChange={handleSelectAll}
                          disabled={filteredCallLogs.length === 0}
                        />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleLogsSort('customer')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Customer</span>
                          {logsSortColumn === 'customer' ? (
                            logsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleLogsSort('phone')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Phone</span>
                          {logsSortColumn === 'phone' ? (
                            logsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleLogsSort('duration')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Talk Time</span>
                          {logsSortColumn === 'duration' ? (
                            logsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleLogsSort('timestamp')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Time</span>
                          {logsSortColumn === 'timestamp' ? (
                            logsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleLogsSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {logsSortColumn === 'status' ? (
                            logsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCallLogs.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-6 py-6 text-center text-sm text-gray-500"
                        >
                          No call logs found for the current filters.
                        </td>
                      </tr>
                    ) : (
                      getSortedCallLogs(filteredCallLogs).map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              checked={selectedRows.has(log.id)}
                              onChange={() => handleSelectRow(log.id)}
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="min-w-0 max-w-xs transition-all">
                              {log.leadId ? (
                                <button
                                  onClick={() => navigate(`/lead_detail_360/${log.leadId}`)}
                                  className="text-sm font-semibold text-agrosaf-green hover:underline capitalize truncate flex items-center gap-1 group"
                                >
                                  {log.customer}
                                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                              ) : log.customerId ? (
                                <button
                                  onClick={() => navigate(`/customer_360/${log.customerId}`)}
                                  className="text-sm font-semibold text-agrosaf-green hover:underline capitalize truncate flex items-center gap-1 group"
                                >
                                  {log.customer}
                                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                              ) : (
                                <div className="text-sm font-semibold text-gray-900 capitalize truncate">
                                  {log.customer}
                                </div>
                              )}
                              <div className="text-sm text-gray-500 capitalize truncate">
                                {log.business}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {log.phone || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="font-semibold text-gray-800">{log.duration || '-'}</span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="flex flex-col">
                              <div className="whitespace-nowrap font-medium">{formatDate(log.timestamp, { includeTime: false })}</div>
                              <div className="whitespace-nowrap text-xs text-gray-500 mt-0.5">
                                {log.timestamp ? new Date(log.timestamp).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                }) : ''}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${log.status === 'completed' || log.status === 'answered' ? 'bg-green-100 text-green-700' :
                              log.status === 'missed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                              {getCallStatusDisplay(log.rpDigitalStatus) || getCallStatusDisplay(log.status) || '-'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              {log.leadId ? (
                                <button
                                  onClick={() => navigate(`/lead_detail_360/${log.leadId}`)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-agrosaf-green text-xs font-medium rounded text-agrosaf-green bg-white hover:bg-green-50 transition-colors"
                                  title="View Lead"
                                >
                                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                  Lead
                                </button>
                              ) : log.customerId ? (
                                <button
                                  onClick={() => navigate(`/customer_360/${log.customerId}`)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-agrosaf-green text-xs font-medium rounded text-agrosaf-green bg-white hover:bg-green-50 transition-colors"
                                  title="View Customer"
                                >
                                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                  Customer
                                </button>
                              ) : (
                                <button
                                  onClick={() => navigate(`/manual_lead_entry?phone=${log.phone}`)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-blue-600 text-xs font-medium rounded text-blue-600 bg-white hover:bg-blue-50 transition-colors"
                                  title="Add as Lead"
                                >
                                  <UserPlus className="h-3.5 w-3.5 mr-1" />
                                  Add Lead
                                </button>
                              )}
                              {log.recording ? (
                                <button
                                  onClick={() => handlePlayRecording(log)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-green-600 text-xs font-medium rounded text-green-600 bg-white hover:bg-green-50 transition-colors"
                                  title="Play Recording"
                                >
                                  <Play className="h-3.5 w-3.5" />
                                </button>
                              ) : (
                                <span className="text-gray-300 -mr-1">
                                  <PhoneOff className="h-4 w-4" />
                                </span>
                              )}
                              <button
                                onClick={() => handleViewDetails(log)}
                                className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                title="View Call Details"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Call Logs Pagination */}
          {(() => {
            const totalItems = Number(callLogsPagination?.totalActivities || callLogs.length || 0);
            const totalPages = Number(callLogsPagination?.totalPages || 1);
            const effectivePaginationLimit = callLogsPagination?.limit || callLogsItemsPerPage || 10;
            // Show pagination if there's more than one page
            const shouldShowPagination = !isCallLogsLoading && totalPages > 1;

            const handleCallLogsPageChange = (page) => {
              if (page >= 1 && page <= totalPages) {
                setCallLogsCurrentPage(page);
              }
            };

            const handleCallLogsItemsPerPageChange = (newLimit) => {
              setCallLogsItemsPerPage(newLimit);
              setCallLogsCurrentPage(1);
            };

            return shouldShowPagination ? (
              <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                  {/* Results Count and Limit Selector */}
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-700">
                      Showing {((callLogsPagination.currentPage || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((callLogsPagination.currentPage || 1) * effectivePaginationLimit, totalItems)} of {totalItems} results
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700">Items per page:</label>
                      <select
                        value={callLogsItemsPerPage}
                        onChange={(e) => handleCallLogsItemsPerPageChange(Number(e.target.value))}
                        className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleCallLogsPageChange((callLogsPagination.currentPage || 1) - 1)}
                      disabled={callLogsPagination.hasPrevPage === false || (callLogsPagination.currentPage || 1) <= 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {(() => {
                        const totalPagesInner = callLogsPagination.totalPages || 1;
                        const currentPageNum = callLogsPagination.currentPage || 1;
                        const pages = [];

                        if (totalPagesInner <= 10) {
                          for (let i = 1; i <= totalPagesInner; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push(1);

                          if (currentPageNum <= 4) {
                            for (let i = 2; i <= 5; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPagesInner);
                          } else if (currentPageNum >= totalPagesInner - 3) {
                            pages.push('ellipsis');
                            for (let i = totalPagesInner - 4; i <= totalPagesInner; i++) {
                              pages.push(i);
                            }
                          } else {
                            pages.push('ellipsis');
                            for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPagesInner);
                          }
                        }

                        return pages.map((page, index) => {
                          if (page === 'ellipsis') {
                            return (
                              <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                ...
                              </span>
                            );
                          }

                          return (
                            <button
                              key={page}
                              onClick={() => handleCallLogsPageChange(page)}
                              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                ? 'bg-agrosaf-green text-white'
                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                              {page}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      onClick={() => handleCallLogsPageChange((callLogsPagination.currentPage || 1) + 1)}
                      disabled={callLogsPagination.hasNextPage === false || (callLogsPagination.currentPage || 1) >= (callLogsPagination.totalPages || 1)}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            ) : null;
          })()}
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title={activeTab === 'missed_calls' ? 'Export Missed Calls' : 'Export Call Logs'}
        showDateRange
        scopedFormatExport={{
          onExport: handleCallerPopupsScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />


      {/* Right column content for Recent Calls when on Live Console (desktop only) */}
      {activeTab === "live_console" && recentCalls.length > 0 && (
        <div className="mt-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">
                Recent Calls
              </h3>
              <span className="text-xs text-gray-500">
                Last {Math.min(recentCalls.length, 10)} calls
              </span>
            </div>
            <div className="space-y-2">
              {recentCalls.slice(0, 10).map((call) => (
                <div
                  key={call.id}
                  className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-b-0"
                >
                  <div className="min-w-0 mr-3">
                    <p className="text-xs font-medium text-gray-900 truncate">
                      {call.customer}
                    </p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {call.phone || '-'}
                    </p>
                  </div>
                  <div className="text-right">
                    {call.startTime && (
                      <p className="text-[11px] text-gray-400">
                        {new Date(call.startTime).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                    {call.status && (
                      <p className="text-[11px] text-gray-500 capitalize">
                        {call.status}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      {/* Filter Modal (shared across tabs) */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                More Filters
              </h3>
              <button
                onClick={handleCloseFilterModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={additionalFilters.startDate}
                  onChange={(e) =>
                    setAdditionalFilters({
                      ...additionalFilters,
                      startDate: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={additionalFilters.endDate}
                  onChange={(e) =>
                    setAdditionalFilters({
                      ...additionalFilters,
                      endDate: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                />
              </div>
              {activeTab !== "missed_calls" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={additionalFilters.priority}
                    onChange={(e) =>
                      setAdditionalFilters({
                        ...additionalFilters,
                        priority: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  >
                    <option value="all">All Priorities</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned To
                </label>
                <select
                  value={additionalFilters.assignedTo}
                  onChange={(e) =>
                    setAdditionalFilters({
                      ...additionalFilters,
                      assignedTo: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                >
                  <option value="all">All Teams</option>
                  <option value="Support Team">Support Team</option>
                  <option value="Sales Team">Sales Team</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Reset
              </button>
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal (for missed calls) */}
      {isEditModalOpen && editCall && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Call</h3>
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setErrors({});
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes *
                </label>
                <textarea
                  value={editCall.notes}
                  onChange={(e) => {
                    const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.notes);
                    setEditCall({ ...editCall, notes: sanitized });
                    if (errors.notes) {
                      setErrors(prev => ({ ...prev, notes: '' }));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.notes ? 'border-red-300' : 'border-gray-300'
                    }`}
                  rows="4"
                  maxLength={FIELD_LIMITS.notes}
                  required
                />
                <div className="text-right mt-1 text-xs text-gray-500">
                  {getCharacterCount(editCall.notes, FIELD_LIMITS.notes)}
                </div>
                {errors.notes && (
                  <p className="text-xs text-red-600 mt-1">{errors.notes}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned To
                </label>
                <select
                  value={editCall.assignedTo}
                  onChange={(e) =>
                    setEditCall({ ...editCall, assignedTo: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                >
                  <option value="Support Team">Support Team</option>
                  <option value="Sales Team">Sales Team</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={editCall.priority}
                  onChange={(e) =>
                    setEditCall({ ...editCall, priority: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal (shared across tabs) */}
      {isDetailsModalOpen && selectedCall && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-xl">
            <div className="flex justify-between items-start px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Call Activity Details
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedCall.type ? `${selectedCall.type} call` : 'Call'} • {formatDate(selectedCall.timestamp, { includeTime: true })}
                </p>
              </div>
              <button
                onClick={handleCloseDetailsModal}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-md hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-132px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Customer</p>
                  <p className="text-sm font-medium text-gray-900 mt-1 break-words">{selectedCall.customer}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Business</p>
                  <p className="text-sm font-medium text-gray-900 mt-1 break-words">{selectedCall.business}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</p>
                  <p className="text-sm font-medium text-gray-900 mt-1 break-words">{selectedCall.phone || '-'}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Time</p>
                  <p className="text-sm font-medium text-gray-900 mt-1 break-words">
                    {formatDate(selectedCall.timestamp, { includeTime: true })}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Start Time</p>
                  <p className="text-sm font-medium text-gray-900 mt-1 break-words">
                    {formatCallMetadataDate(
                      selectedCall.callMetadata?.startTime,
                      selectedCall.callMetadata?.StartTime,
                      selectedCall.callMetadata?.start_time,
                      selectedCall.callMetadata?.startdatetime,
                      selectedCall.callMetadata?.startDateTime,
                      selectedCall.callMetadata?.legBStartTime,
                      selectedCall.callMetadata?.legAStartTime
                    )}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">End Time</p>
                  <p className="text-sm font-medium text-gray-900 mt-1 break-words">
                    {formatCallMetadataDate(
                      selectedCall.callMetadata?.endTime,
                      selectedCall.callMetadata?.EndTime,
                      selectedCall.callMetadata?.end_time,
                      selectedCall.callMetadata?.enddatetime,
                      selectedCall.callMetadata?.endDateTime,
                      selectedCall.callMetadata?.hangupTime
                    )}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Duration</p>
                  <p className="text-sm font-medium text-gray-900 mt-1 break-words">{selectedCall.duration}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</p>
                  <p className="text-sm font-medium text-gray-900 mt-1 break-words">{selectedCall.status}</p>
                </div>
                {selectedCall.priority && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Priority</p>
                    <p className="text-sm font-medium text-gray-900 mt-1 break-words">
                      {selectedCall.priority}
                    </p>
                  </div>
                )}
                {selectedCall.type && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Type</p>
                    <p className="text-sm font-medium text-gray-900 mt-1 break-words">{selectedCall.type}</p>
                  </div>
                )}
                {selectedCall.disposition && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 md:col-span-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Disposition</p>
                    <p className="text-sm font-medium text-gray-900 mt-1 break-words">
                      {selectedCall.disposition}
                    </p>
                  </div>
                )}
                {selectedCall.recording && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 md:col-span-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recording</p>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
                      <button
                        onClick={() => handlePlayRecording(selectedCall)}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-agrosaf-green text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors w-fit"
                      >
                        <Play className="h-4 w-4" />
                        Open Recording
                      </button>
                      <p className="text-sm font-medium text-gray-900 break-all min-w-0">
                        {selectedCall.recording}
                      </p>
                    </div>
                  </div>
                )}
                {selectedCall.assignedTo && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Assigned To
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-1 break-words">
                      {selectedCall.assignedTo}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={handleCloseDetailsModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallerPopups;
